/**
 * ÖVERSIKTEN — den hämtade verkligheten bakom startsidan, kalendern och
 * inställningarna.
 *
 * VARFÖR DEN FINNS. Kaklen ska visa RIKTIG data: hur mycket tid jag lagt in i
 * veckan, hur många arbetsordrar som är öppna, vilka dagar i månaden som har
 * tid på sig. Den enda läsvägen till företagsdata i kontraktet är SYNC_PULL —
 * TODAY är FULL_VIEW_ROLES och släpper inte in en montör, och INVOICE_BASIS
 * likaså. Alltså: en pull, vikt ihop till de tal skärmarna behöver.
 *
 * DET HÄR ÄR INTE STEG 5:s LOKALA DATABAS. Det finns ingen IndexedDB och ingen
 * utkorg än. Modulen börjar därför på `since = 0` vid varje hämtning och håller
 * resultatet i minnet så länge appen är öppen. Två följder, båda avsiktliga:
 *   - VYBYTET är gratis. SyncPullResponse kräver att klienten TÖMMER allt och
 *     börjar om på since = 0 när `view` ändras (en befordrad montör, en
 *     degraderad chef). En modul som alltid börjar på 0 kan inte hamna fel.
 *   - Firman laddas om från början vid varje kallstart. För en firma med fem
 *     man är det några hundra rader. När steg 5:s lager finns byts `hamta()`
 *     mot en läsning ur det, och ingen skärm rör sig.
 *
 * BESLUT 16 HÅLLS AV TYPERNA. `lagg()` grenar på `view` och de tre vikarna tar
 * kontraktets egna radtyper — `TimeEntryRow | TimeEntryWorkerRow` och inte en
 * egen spegel. Ett försök att läsa rate_ore_per_hour i den funktionen är ett
 * kompileringsfel, för fältet finns inte på halva unionen. Ingen siffra i den
 * här filen är ett pris.
 *
 * BESLUT 04 HÅLLS AV RÄKNINGEN. Varje SUMMA härnedan är per ISO-VECKA.
 * Månadsrutnätet i kalendern är NAVIGERING — ett sätt att bläddra sig till en
 * dag — och får därför ingen månadstotal; räkningen i dess huvud är ANTAL
 * DAGAR med tid, vilket beskriver rutnätet och inte en period.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { SYNC_PULL } from '@delat/routes';
import { APPROVED_WEEK_STATES, asSeq, asWorkDate } from '@delat/types';
import type {
  AbsenceKind,
  AbsenceRow,
  AccountId,
  ClientView,
  CompanyId,
  CustomerRow,
  CustomerWorkerRow,
  ExpenseRow,
  ExpenseWorkerRow,
  IsoWeek,
  MemberRow,
  MemberWorkerRow,
  MileageRow,
  MileageWorkerRow,
  PriceListRow,
  PriceRateRow,
  Role,
  Seq,
  SubscriptionStatus,
  SyncPullResponse,
  TaskRow,
  TimeEntryRow,
  TimeEntryWorkerRow,
  WeekState,
  WeekStatusRow,
  WorkDate,
  WorkOrderRow,
  WorkOrderWorkerRow,
} from '@delat/types';
import { klienten } from '../api';
import type { Klartidfel } from '../api';
import { granskaSyncPull } from '../api/granskare/synk';
import { registreraFirmaminne } from '../app/firmaminne';
import { useInloggad } from '../app/session';
import {
  arHelg,
  dagarIVecka,
  plusDagar,
  tillArbetsdag,
  veckaFor,
  veckodag,
  visaMinuter,
} from '../app/datum';
import type { Dagston, Manadsdag } from '../ui';

/* == Vad skärmarna får ut ================================================ */

/** En av mina tidrader, redan sammansatt med jobbet den hör till. */
export interface Tidpost {
  readonly id: string;
  readonly dag: WorkDate;
  readonly minuter: number;
  /** Adressen om den finns, annars beskrivningen. Så känner man igen ett jobb. */
  readonly rubrik: string;
  /** Kundens namn, när kunden gått ut till den här vyn. */
  readonly kund: string | null;
}

export interface Utlaggspost {
  readonly antal: number;
  /** EX MOMS, som allt i kontraktet. */
  readonly oren: number;
}

export interface Oversikt {
  readonly vy: ClientView;
  /** Mina levande tidrader per arbetsdag. */
  readonly tidPerDag: ReadonlyMap<string, readonly Tidpost[]>;
  /** Summan av mina minuter per arbetsdag. */
  readonly minuterPerDag: ReadonlyMap<string, number>;
  /** Min frånvaro per arbetsdag. En dag med frånvaro väntar inte på något. */
  readonly franvaroPerDag: ReadonlyMap<string, AbsenceKind>;
  /** Mitt veckoläge per ISO-vecka. Saknas veckan är den 'open'. */
  readonly veckolagen: ReadonlyMap<string, WeekState>;
  /** Mina milmeter per arbetsdag. */
  readonly meterPerDag: ReadonlyMap<string, number>;
  /** Mina utlägg per arbetsdag. */
  readonly utlaggPerDag: ReadonlyMap<string, Utlaggspost>;
  /** Levande arbetsordrar med status 'open'. */
  readonly oppnaOrdrar: number;
  /** Av dem: de jag står som ansvarig för. En SORTERING, aldrig en gräns. */
  readonly minaOppnaOrdrar: number;

  /* -- FÖRUTSÄTTNINGARNA, de tre saker som måste finnas innan en enda timme
        går att spara. De låg tidigare bara i sina egna skärmar, vilket gjorde
        att startsidan visade en tom app utan att kunna säga VARFÖR den var tom
        — och att ett saknat timpris upptäcktes först som PRICE_MISSING från
        servern, efter att allt var ifyllt. Se kravenFor() längre ner. ------ */

  /** Firmans levande moment finns. Utan ett enda går ingen timme att spara. */
  readonly harMoment: boolean;
  /**
   * Finns ett timpris för MIN roll i firmans standardlista?
   * null = går inte att veta i den här vyn: montörs- och gästvyn saknar
   * price_rate och price_list helt (beslut 16). Då ska ingenting påstås.
   */
  readonly harMittTimpris: boolean | null;
  /** Levande medlemmar, jag inräknad. 1 = jag är ensam i firman. */
  readonly antalMedlemmar: number;
  /**
   * Dagen jag kom med i firman, ur min medlemsrads server_received_at.
   * Dagar FÖRE den dagen är inte min skuld och får aldrig räknas som saknade —
   * en nyfödd firma mötte annars "4 dagar saknas" på första sekunden.
   */
  readonly minStartdag: WorkDate | null;

  /** Så många varv pulln tog. > 1 betyder att firman har historik. */
  readonly avkortad: boolean;
}

export type Oversiktslage =
  | { readonly slag: 'laddar' }
  | { readonly slag: 'klar'; readonly oversikt: Oversikt }
  | { readonly slag: 'fel'; readonly fel: Klartidfel };

/* == Talet som styr kalenderns färger ==================================== */

/**
 * VAD SOM RÄKNAS SOM EN HEL DAG I KALENDERN. Sju och en halv timme, den
 * svenska heltidsnormen.
 *
 * DEN ÄR ETT VISNINGSTRÖSKELVÄRDE OCH INGENTING ANNAT: den avgör om en dag får
 * grön eller gul prick, och den rör aldrig en summa, ett fakturaunderlag eller
 * en attest. Kontraktet har inget mått på en arbetsdag — det hör hemma i
 * company_settings, se LUCKOR i överlämningen. Tills det finns står talet HÄR,
 * på ett ställe, i stället för i fyra skärmar.
 */
export const NORMAL_ARBETSDAG_MINUTER = 450;

/* == Vikningen =========================================================== */

interface Order {
  readonly rubrik: string;
  readonly kundId: string;
  readonly oppen: boolean;
  readonly minAnsvar: boolean;
}

interface Bygge {
  vy: ClientView | null;
  avkortad: boolean;
  /** customer_id -> namn. Levande kunder. */
  readonly kunder: Map<string, string>;
  readonly ordrar: Map<string, Order>;
  /** time_entry_id -> raden. Bara MINA. */
  readonly tid: Map<string, { dag: WorkDate; minuter: number; order: string }>;
  readonly mil: Map<string, { dag: WorkDate; meter: number }>;
  readonly utlagg: Map<string, { dag: WorkDate; oren: number }>;
  readonly franvaro: Map<string, { dag: WorkDate; slag: AbsenceKind }>;
  /** iso_week -> läge. Bara MITT. */
  readonly veckor: Map<string, WeekState>;
  /** task_id -> namnet. Bara antalet används; kartan finns för gravstenarna. */
  readonly moment: Map<string, string>;
  /** account_id -> min startdag om raden är MIN, annars null. Levande rader. */
  readonly medlemmar: Map<string, WorkDate | null>;
  /**
   * MIN roll, läst ur min egen medlemsrad och inte ur sessionen. Kontraktet är
   * uttryckligt: "ROLLEN LÄSES ALLTID UR D1 (membership)". Samma pull som bär
   * prisraderna bär rollen, så de två kan inte glida isär mitt i en hämtning.
   */
  minRoll: Role | null;
  /**
   * price_list_id för firmans standardlista (is_default = 1). null i
   * montörs- och gästvy, som inte får price_list alls.
   */
  standardlista: string | null;
  /** price_rate_id -> { lista, roll }. Levande rader, full vy bara. */
  readonly priser: Map<string, { lista: string; roll: Role }>;
  /** true = vyn bar price_list/price_rate, alltså går priset att pröva. */
  prisdataFinns: boolean;
}

const nyttBygge = (): Bygge => ({
  vy: null,
  avkortad: false,
  kunder: new Map(),
  ordrar: new Map(),
  tid: new Map(),
  mil: new Map(),
  utlagg: new Map(),
  franvaro: new Map(),
  veckor: new Map(),
  moment: new Map(),
  medlemmar: new Map(),
  minRoll: null,
  standardlista: null,
  priser: new Map(),
  prisdataFinns: false,
});

/**
 * En rad som kommit tillbaka med deleted_at satt är en GRAVSTEN och ska tas
 * bort ur bygget, inte läggas till. Sidorna kommer i seq-ordning, så en senare
 * sida skriver alltid över en tidigare.
 */
const skrivEllerRadera = <T>(
  karta: Map<string, T>,
  nyckel: string,
  raderad: boolean,
  varde: () => T,
): void => {
  if (raderad) karta.delete(nyckel);
  else karta.set(nyckel, varde());
};

const laggKunder = (b: Bygge, rader: readonly (CustomerRow | CustomerWorkerRow)[]): void => {
  for (const r of rader) {
    skrivEllerRadera(b.kunder, r.id, r.deleted_at !== null, () => r.name);
  }
};

const laggOrdrar = (
  b: Bygge,
  rader: readonly (WorkOrderRow | WorkOrderWorkerRow)[],
  jag: AccountId,
): void => {
  for (const r of rader) {
    skrivEllerRadera(b.ordrar, r.id, r.deleted_at !== null, () => ({
      // Adressen först: det är så en hantverkare känner igen ett jobb.
      rubrik: r.address ?? r.description,
      kundId: r.customer_id,
      oppen: r.status === 'open',
      minAnsvar: r.responsible_account_id === jag,
    }));
  }
};

/**
 * MINA tidrader. I montörs- och gästvy skickar servern bara mina ändå; i full
 * vy kommer hela firmans, och startsidan handlar om MITT jobb.
 */
const laggTid = (
  b: Bygge,
  rader: readonly (TimeEntryRow | TimeEntryWorkerRow)[],
  jag: AccountId,
): void => {
  for (const r of rader) {
    if (r.account_id !== jag) continue;
    skrivEllerRadera(b.tid, r.id, r.deleted_at !== null, () => ({
      dag: r.work_date,
      minuter: r.minutes,
      order: r.work_order_id,
    }));
  }
};

const laggMil = (
  b: Bygge,
  rader: readonly (MileageRow | MileageWorkerRow)[],
  jag: AccountId,
): void => {
  for (const r of rader) {
    if (r.account_id !== jag) continue;
    skrivEllerRadera(b.mil, r.id, r.deleted_at !== null, () => ({
      dag: r.work_date,
      meter: r.meters,
    }));
  }
};

const laggUtlagg = (
  b: Bygge,
  rader: readonly (ExpenseRow | ExpenseWorkerRow)[],
  jag: AccountId,
): void => {
  for (const r of rader) {
    if (r.account_id !== jag) continue;
    skrivEllerRadera(b.utlagg, r.id, r.deleted_at !== null, () => ({
      dag: r.work_date,
      oren: r.amount_ore,
    }));
  }
};

const laggFranvaro = (b: Bygge, rader: readonly AbsenceRow[], jag: AccountId): void => {
  for (const r of rader) {
    if (r.account_id !== jag) continue;
    skrivEllerRadera(b.franvaro, r.id, r.deleted_at !== null, () => ({
      dag: r.work_date,
      slag: r.kind,
    }));
  }
};

const laggVeckor = (b: Bygge, rader: readonly WeekStatusRow[], jag: AccountId): void => {
  for (const r of rader) {
    if (r.account_id !== jag) continue;
    skrivEllerRadera(b.veckor, r.iso_week, r.deleted_at !== null, () => r.state);
  }
};

/* -- De tre förutsättningarna. Alla tre tabellerna finns i BÅDA grenarna av
      ChangeSet utom price_list/price_rate, som är full vy bara (beslut 16). -- */

const laggMoment = (b: Bygge, rader: readonly TaskRow[]): void => {
  for (const r of rader) {
    skrivEllerRadera(b.moment, r.id, r.deleted_at !== null, () => r.name);
  }
};

/**
 * MEDLEMMARNA, och MIN startdag ur min egen rad.
 *
 * server_received_at är när raden FÖRST nådde servern och ändras aldrig av en
 * senare skrivning (SyncColumns) — alltså dagen jag kom med i firman, även om
 * min roll ändrats sedan. Det är det enda ankaret i kontraktet som svarar på
 * frågan "sedan när är det här mitt ansvar?", och det är precis den frågan
 * "N dagar saknas" egentligen ställer.
 */
const laggMedlemmar = (
  b: Bygge,
  rader: readonly (MemberRow | MemberWorkerRow)[],
  jag: AccountId,
): void => {
  for (const r of rader) {
    skrivEllerRadera(b.medlemmar, r.account_id, r.deleted_at !== null, () =>
      r.account_id === jag ? tillArbetsdag(r.server_received_at) : null,
    );
    if (r.account_id === jag && r.deleted_at === null) b.minRoll = r.role;
  }
};

const laggPriser = (
  b: Bygge,
  listor: readonly PriceListRow[],
  priser: readonly PriceRateRow[],
): void => {
  b.prisdataFinns = true;
  for (const r of listor) {
    if (r.deleted_at === null && r.is_default === 1) b.standardlista = r.id;
  }
  for (const r of priser) {
    skrivEllerRadera(b.priser, r.id, r.deleted_at !== null, () => ({
      lista: r.price_list_id,
      roll: r.role,
    }));
  }
};

/**
 * EN SIDA UR PULLN. Grenandet på `view` är beslut 16 och 17 i praktiken: i
 * montörs- och gästgrenen finns prisfälten inte på raderna alls, så ingen
 * summa härnedan kan räkna bakvägen till ett timpris.
 */
const lagg = (b: Bygge, svar: SyncPullResponse, jag: AccountId): void => {
  b.vy = svar.view;
  if (svar.view === 'full') {
    const c = svar.changes;
    laggKunder(b, c.customer);
    laggOrdrar(b, c.work_order, jag);
    laggTid(b, c.time_entry, jag);
    laggMil(b, c.mileage, jag);
    laggUtlagg(b, c.expense, jag);
    laggFranvaro(b, c.absence, jag);
    laggVeckor(b, c.week_status, jag);
    laggMoment(b, c.task);
    laggMedlemmar(b, c.member, jag);
    // BARA HÄR. Grenen nedanför har inga prisrader att läsa, och det är hela
    // beslut 16 — därför står `prisdataFinns` kvar som false där, och
    // `harMittTimpris` blir null i stället för false. En montör ska inte få
    // höra att firman saknar ett timpris han inte får se.
    laggPriser(b, c.price_list, c.price_rate);
    return;
  }
  const c = svar.changes;
  laggKunder(b, c.customer);
  laggOrdrar(b, c.work_order, jag);
  laggTid(b, c.time_entry, jag);
  laggMil(b, c.mileage, jag);
  laggUtlagg(b, c.expense, jag);
  laggFranvaro(b, c.absence, jag);
  laggVeckor(b, c.week_status, jag);
  laggMoment(b, c.task);
  laggMedlemmar(b, c.member, jag);
};

const sammanstall = (b: Bygge): Oversikt => {
  const tidPerDag = new Map<string, Tidpost[]>();
  const minuterPerDag = new Map<string, number>();
  for (const [id, rad] of b.tid) {
    const order = b.ordrar.get(rad.order);
    const kundnamn = order === undefined ? null : (b.kunder.get(order.kundId) ?? null);
    const post: Tidpost = {
      id,
      dag: rad.dag,
      minuter: rad.minuter,
      rubrik: order?.rubrik ?? 'Arbetsorder',
      kund: kundnamn,
    };
    const rader = tidPerDag.get(rad.dag);
    if (rader === undefined) tidPerDag.set(rad.dag, [post]);
    else rader.push(post);
    minuterPerDag.set(rad.dag, (minuterPerDag.get(rad.dag) ?? 0) + rad.minuter);
  }

  const meterPerDag = new Map<string, number>();
  for (const rad of b.mil.values()) {
    meterPerDag.set(rad.dag, (meterPerDag.get(rad.dag) ?? 0) + rad.meter);
  }

  const utlaggPerDag = new Map<string, Utlaggspost>();
  for (const rad of b.utlagg.values()) {
    const forra = utlaggPerDag.get(rad.dag) ?? { antal: 0, oren: 0 };
    utlaggPerDag.set(rad.dag, { antal: forra.antal + 1, oren: forra.oren + rad.oren });
  }

  const franvaroPerDag = new Map<string, AbsenceKind>();
  for (const rad of b.franvaro.values()) franvaroPerDag.set(rad.dag, rad.slag);

  let oppna = 0;
  let mina = 0;
  for (const order of b.ordrar.values()) {
    if (!order.oppen) continue;
    oppna += 1;
    if (order.minAnsvar) mina += 1;
  }

  /*
    TIMPRISET FÖR MIN ROLL, och de tre stegen i uppslaget är alla tre viktiga:
      1. Bar vyn prisdata alls? Nej -> null. Ingenting påstås om det vi inte
         fick se (beslut 16).
      2. Har min roll ett pris? Bokföraren rapporterar inte och har aldrig ett
         (PriceRole utesluter honom), så för honom är frågan inte tillämplig.
      3. Ligger priset i STANDARDLISTAN? Ett pris i en arkiverad lista är inte
         det pris servern slår upp, och skulle ha gjort kravet tyst grönt medan
         PRICE_MISSING ändå kom.
  */
  const harMittTimpris: boolean | null =
    !b.prisdataFinns || b.minRoll === null || b.minRoll === 'bookkeeper'
      ? null
      : b.standardlista !== null &&
        [...b.priser.values()].some(
          (p) => p.lista === b.standardlista && p.roll === b.minRoll,
        );

  return {
    vy: b.vy ?? 'worker',
    tidPerDag,
    minuterPerDag,
    franvaroPerDag,
    veckolagen: b.veckor,
    meterPerDag,
    utlaggPerDag,
    oppnaOrdrar: oppna,
    minaOppnaOrdrar: mina,
    harMoment: b.moment.size > 0,
    harMittTimpris,
    antalMedlemmar: b.medlemmar.size,
    minStartdag: [...b.medlemmar.values()].find((d) => d !== null) ?? null,
    avkortad: b.avkortad,
  };
};

/* == Hämtningen ========================================================== */

/**
 * Så många pull-varv vi tar innan vi visar det vi har. Med
 * SYNC_PULL_DEFAULT_LIMIT rader per varv räcker det för en liten firmas hela
 * historik; tar det slut visas det som hämtats och `avkortad` säger ifrån.
 */
const MAX_VARV = 40;

const hamta = async (
  firma: CompanyId,
  jag: AccountId,
  signal: AbortSignal,
): Promise<Oversiktslage> => {
  const b = nyttBygge();
  let since: Seq = asSeq(0);
  for (let varv = 0; varv < MAX_VARV; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      { params: { company_id: firma }, data: { since }, signal },
      granskaSyncPull,
    );
    if (!svar.ok) return { slag: 'fel', fel: svar.fel };
    lagg(b, svar.varde, jag);
    // has_more: true MED tom changes är normalt i montörs- och gästläge —
    // markören, inte antalet rader, säger om det finns mer.
    if (!svar.varde.has_more) return { slag: 'klar', oversikt: sammanstall(b) };
    if (svar.varde.next_since <= since) {
      // Markören står stilla. Att fråga igen ger samma svar i evighet.
      b.avkortad = true;
      return { slag: 'klar', oversikt: sammanstall(b) };
    }
    since = svar.varde.next_since;
  }
  b.avkortad = true;
  return { slag: 'klar', oversikt: sammanstall(b) };
};

/* == Lagret bakom kroken ================================================= */

const LADDAR: Oversiktslage = { slag: 'laddar' };

interface Post {
  readonly nyckel: string;
  lage: Oversiktslage;
  styrning: AbortController | null;
}

let post: Post | null = null;
const lyssnare = new Set<() => void>();

const ropa = (): void => {
  for (const l of lyssnare) l();
};

const prenumerera = (l: () => void): (() => void) => {
  lyssnare.add(l);
  return () => {
    lyssnare.delete(l);
  };
};

const nyckelFor = (firma: CompanyId, jag: AccountId): string => `${firma}/${jag}`;

const sakerstall = (firma: CompanyId, jag: AccountId): void => {
  const nyckel = nyckelFor(firma, jag);
  if (post !== null && post.nyckel === nyckel) return;
  post?.styrning?.abort();
  const styrning = new AbortController();
  const min: Post = { nyckel, lage: LADDAR, styrning };
  post = min;
  ropa();
  void (async () => {
    const lage = await hamta(firma, jag, styrning.signal);
    // En hämtning som hunnits bytas ut (annan firma, omladdning) får inte
    // skriva över den nya.
    if (post !== min) return;
    min.lage = lage;
    min.styrning = null;
    ropa();
  })();
};

/** "Försök igen" och efter en sparning: kasta det hämtade och hämta om. */
export const laddaOmOversikt = (firma: CompanyId, jag: AccountId): void => {
  post?.styrning?.abort();
  post = null;
  sakerstall(firma, jag);
};

/**
 * FIRMAMINNET — översikten var det enda datalagret som INTE hade hakat på.
 *
 * Följden var en tyst lucka som inte hörde till dag ett men syntes tydligast
 * där: `glomAllaFirmaminnen()` efter en lyckad SYNC_PUSH tömde arbetsorder-,
 * moment-, pris- och tidrapportlagret — men inte det här. Startsidan ritade
 * alltså vidare på en översikt hämtad FÖRE skrivningen, i upp till trettio
 * sekunder: lägg upp en arbetsorder i NyOrder och Arbetsordrar-kaklet stod kvar
 * på "Inga öppna just nu"; gå igenom Kom igång och kortet "Innan du kan
 * rapportera" stod kvar med tre steg fast alla tre var gjorda. Samma
 * modulvariabel levde dessutom över en utloggning i samma flik, vilket är
 * precis den läcka firmaminne.ts finns för.
 *
 * DEN HÄMTAR INTE OM SJÄLV, och det är avsiktligt. Registret töms vid
 * UTLOGGNING och FIRMABYTE (app/session.tsx), och i båda lägena vore en ny
 * hämtning med det gamla lagrets nycklar fel: den ena mot en session som inte
 * finns, den andra mot firman man just lämnade. Den nollar, ropar, och låter
 * kroken nedan hämta det som den som faktiskt tittar behöver.
 */
const glomOversikten = (): void => {
  post?.styrning?.abort();
  post = null;
  ropa();
};

registreraFirmaminne(glomOversikten);

/**
 * Översikten för den inloggades firma. Alla skärmar som anropar den delar EN
 * hämtning — startsidan och kalendern hämtar inte var sin.
 */
export const useOversikt = (): Oversiktslage => {
  const { firmaId, konto } = useInloggad();
  const nyckel = nyckelFor(firmaId, konto.id);
  const lage = useSyncExternalStore(prenumerera, () =>
    post !== null && post.nyckel === nyckel ? post.lage : LADDAR,
  );
  /*
    Efter prenumerationen, så att en hämtning som blir klar direkt ändå når
    fram. useSyncExternalStore läser om ögonblicksbilden när den kopplar upp
    sig, så ordningen är säkrad även åt andra hållet.

    INGEN BEROENDELISTA, och det är inte en slarvig utelämning. Med
    [firmaId, konto.id] körde effekten bara när man bytte firma eller konto —
    alltså inte när `glomOversikten()` nollade lagret under en monterad
    startsida. Snapshotet föll då till LADDAR, komponenten ritade om, och
    ingenting hämtade någonsin igen: startsidan stod kvar på skelettet efter
    varje sparning som tömde firmaminnet.

    Att köra efter VARJE render är billigt eftersom `sakerstall` är idempotent —
    har lagret redan rätt nyckel är den en strängjämförelse och ett return, utan
    anrop och utan tillståndsändring. Och den kan inte loopa: ett fel skrivs in
    i samma post, så nästa varv ser rätt nyckel och gör ingenting.
  */
  useEffect(() => {
    sakerstall(firmaId, konto.id);
  });
  return lage;
};

/* == Räkningar skärmarna gör ============================================= */

const summa = (karta: ReadonlyMap<string, number>, dagar: readonly WorkDate[]): number => {
  let n = 0;
  for (const dag of dagar) n += karta.get(dag) ?? 0;
  return n;
};

/** Mina minuter i en ISO-vecka. Beslut 04: veckan är periodenheten. */
export const minuterIVecka = (o: Oversikt, vecka: IsoWeek): number =>
  summa(o.minuterPerDag, dagarIVecka(vecka));

export const meterIVecka = (o: Oversikt, vecka: IsoWeek): number =>
  summa(o.meterPerDag, dagarIVecka(vecka));

export const utlaggIVecka = (o: Oversikt, vecka: IsoWeek): Utlaggspost => {
  let antal = 0;
  let oren = 0;
  for (const dag of dagarIVecka(vecka)) {
    const post = o.utlaggPerDag.get(dag);
    if (post === undefined) continue;
    antal += post.antal;
    oren += post.oren;
  }
  return { antal, oren };
};

/** Veckans läge. En vecka utan rad är 'open' — se week_status i company.sql. */
export const veckolage = (o: Oversikt, vecka: IsoWeek): WeekState =>
  o.veckolagen.get(vecka) ?? 'open';

/**
 * Vardagar i veckan som gått och saknar både tid och frånvaro. DE är vad som
 * väntar på dig — notissiffran på Tidrapport-kaklet är den här längden, aldrig
 * en total.
 *
 * DEN FRÅGAR `dagston` OCH RÄKNAR INTE SJÄLV. Kaklets siffra och kalenderns
 * streckade ringar måste svara likadant; två räkningar hade förr eller senare
 * sagt olika saker om samma dag.
 */
export const dagarSomSaknas = (
  o: Oversikt,
  vecka: IsoWeek,
  nu: WorkDate,
): readonly WorkDate[] => dagarIVecka(vecka).filter((dag) => dagston(o, dag, nu) === 'saknas');

/* == Dag ett: vad som saknas innan en timme går att spara ================= */

export type Kravslag = 'timpris' | 'moment' | 'jobb';

export interface Krav {
  readonly slag: Kravslag;
  readonly rubrik: string;
  /** En rad som säger VAD saken är, inte vad knappen gör. */
  readonly under: string;
}

/**
 * DE TRE FÖRUTSÄTTNINGARNA, I DEN ORDNING DE SKA GÖRAS.
 *
 * Det här är svaret på appens värsta fel: den byggdes för en firma som redan är
 * igång. Från "firman upplagd" till "en timme sparad" krävs ett timpris för min
 * roll, minst ett moment och en arbetsorder — tre saker som alla låg bakom
 * kugghjulet, utan att något pekade dit, och som var för sig gav ett eget
 * avslag långt inne i tidrapporten. Timpriset gav dessutom inget avslag alls
 * förrän SERVERN svarade PRICE_MISSING, med formuläret redan ifyllt.
 *
 * ORDNINGEN ÄR INTE FRI. Den följer vad som blockerar vad: utan pris fryser
 * ingen rad ett belopp, utan moment finns ingen text på fakturan, utan
 * arbetsorder finns ingenting att rapportera PÅ. Kom-igång-skärmen frågar i
 * samma ordning, och startsidans kort räknar i samma ordning.
 *
 * TOM LISTA BETYDER "INGENTING ATT SÄGA", och det gäller tre helt olika lägen
 * som alla ska se likadana ut utifrån: allt finns redan, vyn saknar prisdata
 * (montör och gäst — beslut 16), eller den som frågar inte får skriva
 * stamdata ändå. Att visa en montör tre saker han inte har rutter för hade
 * varit ett gränssnitt som erbjuder och sedan säger nej.
 */
export const kravenFor = (o: Oversikt | null, farSkrivaStamdata: boolean): readonly Krav[] => {
  if (o === null || !farSkrivaStamdata) return [];
  const krav: Krav[] = [];
  // harMittTimpris === null: vyn bar ingen prisdata. Inget påstående görs.
  if (o.harMittTimpris === false) {
    krav.push({
      slag: 'timpris',
      rubrik: 'Sätt ditt timpris',
      under: 'Kronor per timme ex moms. Utan det fryser ingen tidrad ett belopp.',
    });
  }
  if (!o.harMoment) {
    krav.push({
      slag: 'moment',
      rubrik: 'Välj vad du gör',
      under: 'Momenten är texterna som hamnar på kundens faktura.',
    });
  }
  if (o.oppnaOrdrar === 0) {
    krav.push({
      slag: 'jobb',
      rubrik: 'Lägg upp ditt första jobb',
      under: 'Kund och adress. Tiden rapporteras alltid på ett jobb.',
    });
  }
  return krav;
};

/* == Månaden som rutnät (NAVIGERING, inte en period) ===================== */

export interface Manad {
  readonly ar: number;
  /** 1-12. */
  readonly manad: number;
}

export const manadFor = (dag: WorkDate): Manad => ({
  ar: Number(dag.slice(0, 4)),
  manad: Number(dag.slice(5, 7)),
});

export const plusManader = ({ ar, manad }: Manad, steg: number): Manad => {
  const n = ar * 12 + (manad - 1) + steg;
  return { ar: Math.floor(n / 12), manad: (n % 12) + 1 };
};

/**
 * "September". Ren navigeringsetikett.
 *
 * DEN LIGGER HÄR OCH INTE I app/datum.ts, där all annan datumtext bor, av ett
 * skäl: datum.ts är stommens fil och månaden är med FLIT inte ett begrepp i
 * tidrapporteringen (beslut 04). Behövs månadsnamnet av fler än mina tre
 * skärmar hör det ändå hemma där — se LUCKOR i överlämningen.
 */
export const visaManadsnamn = ({ ar, manad }: Manad): string => {
  const namn = new Intl.DateTimeFormat('sv-SE', { timeZone: 'UTC', month: 'long' }).format(
    new Date(Date.UTC(ar, manad - 1, 1)),
  );
  return `${namn.charAt(0).toUpperCase()}${namn.slice(1)}`;
};

/** "September 2026". */
export const visaManad = (m: Manad): string => `${visaManadsnamn(m)} ${m.ar}`;

/**
 * Månadens arbetsdagar i ordning, och hur många blanka rutor som går före den
 * första så att den hamnar under rätt veckodag. `tomma` är måndagsavståndet,
 * alltså ISO:s veckodag och inte JavaScripts.
 */
export const manadensDagar = ({
  ar,
  manad,
}: Manad): { readonly dagar: readonly WorkDate[]; readonly tomma: number } => {
  // Dag 0 i nästa månad är sista dagen i den här.
  const antal = new Date(Date.UTC(ar, manad, 0)).getUTCDate();
  const forsta = asWorkDate(
    `${String(ar).padStart(4, '0')}-${String(manad).padStart(2, '0')}-01`,
  );
  return {
    dagar: Array.from({ length: antal }, (_, i) => plusDagar(forsta, i)),
    tomma: veckodag(forsta),
  };
};

/**
 * DAGENS TON I KALENDERN. Fyra lägen, och de säger fyra olika saker:
 *
 *   'ok'      hel dag rapporterad          --good
 *   'kort'    något rapporterat, men lite  --warn
 *   'saknas'  vardag som gått utan både tid och frånvaro  --accent, streckad
 *   'tom'     inget förväntas — helg, framtid, eller frånvaro är inlagd
 *
 * FRÅNVARO GÖR EN DAG TOM, INTE SAKNAD. Den som lagt in semester har svarat på
 * frågan; att då visa en streckad ring vore att fråga en gång till.
 *
 * EN ATTESTERAD VECKA SAKNAR INGENTING. Står veckan i APPROVED_WEEK_STATES —
 * kontraktets egen mängd för "attesterat", beslut 05 — har chefen redan sagt ja
 * till den, och dess tomma dagar är avgjorda och inte obesvarade. Utan den
 * regeln fylls varje gammal månad av streckade ringar för dagar ingen längre
 * kan eller ska göra något åt, och siffran "N dagar saknas" slutar betyda
 * något. 'reopened' står med FLIT inte i mängden: en återöppnad vecka är
 * öppnad just för att något fattas i den.
 */
export const dagston = (o: Oversikt | null, dag: WorkDate, nu: WorkDate): Dagston => {
  if (o === null) return 'tom';
  const m = o.minuterPerDag.get(dag) ?? 0;
  if (m > 0) return m >= NORMAL_ARBETSDAG_MINUTER ? 'ok' : 'kort';
  if (o.franvaroPerDag.has(dag)) return 'tom';
  /*
    IDAG SAKNAS INTE. Villkoret var `dag > nu`, alltså räknades dagens datum som
    en skuld — från midnatt, innan arbetsdagen ens börjat. En firma som lades upp
    kvart över åtta möttes av "1 dag saknas" med en röd notissiffra på
    Tidrapport-kaklet, och en montör som öppnar appen på morgonen ser samma sak
    varje dag han inte hunnit rapportera än.

    KODEN SA REDAN VAD DEN SKULLE GÖRA: dagarSomSaknas beskriver sig som
    "vardagar i veckan SOM GÅTT", och idag har inte gått. 'saknas' betyder
    försenad, och man kan inte vara försenad med en dag man står i. Rapporteras
    ingenting blir dagen saknad i morgon, vilket är när den faktiskt är det.
  */
  if (dag >= nu || arHelg(dag)) return 'tom';
  /*
    DAGAR FÖRE JAG KOM MED I FIRMAN SAKNAS INTE — de var inte mina.

    Det här var det första en ny firma såg: lägg upp firman en torsdag och
    startsidan svarade "3 dagar saknas" med en röd notissiffra på
    Tidrapport-kaklet, för måndag till onsdag var vardagar som gått utan tid.
    Skulden var två minuter gammal och ändå redan brun. Samma sak möter varje
    montör som bjuds in mitt i en vecka.

    Ankaret är min egen medlemsrads server_received_at (laggMedlemmar), inte
    firmans ålder: en montör som kom in i augusti ska inte ärva firmans juni.
    Saknas ankaret — en pull som inte bar min medlemsrad — räknas som förut,
    hellre det än att tysta en riktig skuld.
  */
  if (o.minStartdag !== null && dag < o.minStartdag) return 'tom';
  const lage = veckolage(o, veckaFor(dag));
  return APPROVED_WEEK_STATES.some((s) => s === lage) ? 'tom' : 'saknas';
};

/**
 * Månaden som rutor åt både det lilla rutnätet på startsidan och det stora på
 * kalenderskärmen. `medEtikett` är det stora rutnätets bricka under datumet;
 * det lilla har bara en prick och ska inte få en text.
 */
export const rutnatFor = (
  o: Oversikt | null,
  manad: Manad,
  nu: WorkDate,
  medEtikett: boolean,
): { readonly dagar: readonly Manadsdag[]; readonly tomma: number } => {
  const { dagar, tomma } = manadensDagar(manad);
  return {
    tomma,
    dagar: dagar.map((dag) => {
      const ton = dagston(o, dag, nu);
      const minuter = o?.minuterPerDag.get(dag) ?? 0;
      const franvaro = o?.franvaroPerDag.get(dag);
      const etikett =
        !medEtikett || o === null
          ? undefined
          : minuter > 0
            ? visaMinuter(minuter)
            : franvaro !== undefined
              ? FRANVARONAMN[franvaro]
              : ton === 'saknas'
                ? 'saknas'
                : undefined;
      return { dag, ton, etikett, idag: dag === nu, helg: arHelg(dag) };
    }),
  };
};

/** Så många dagar i den visade månaden som har tid på sig. */
export const dagarMedTid = (o: Oversikt | null, manad: Manad): number =>
  o === null
    ? 0
    : manadensDagar(manad).dagar.filter((dag) => (o.minuterPerDag.get(dag) ?? 0) > 0).length;

/* == Små svenska etiketter de tre skärmarna delar ======================== */

export const ROLLNAMN: Readonly<Record<Role, string>> = {
  manager: 'chef',
  worker: 'montör',
  apprentice: 'lärling',
  guest: 'inhyrd',
  bookkeeper: 'bokförare',
};

export const PRENUMERATIONSNAMN: Readonly<Record<SubscriptionStatus, string>> = {
  trialing: 'provperiod',
  active: 'aktiv',
  past_due: 'obetald faktura',
  locked: 'pausad',
};

export const FRANVARONAMN: Readonly<Record<AbsenceKind, string>> = {
  vacation: 'semester',
  leave: 'ledig',
  other: 'frånvaro',
};

export const VECKOLAGESNAMN: Readonly<Record<WeekState, string>> = {
  open: 'öppen',
  submitted: 'inlämnad',
  approved: 'attesterad',
  locked: 'låst',
  reopened: 'återöppnad',
};
